/**
 * Search, filter and honest counting for the Registered People drawer.
 *
 * ─── WHY THIS EXISTS ───
 * The registry was a flat fetch of the first 100 people with no controls at all. On a site with
 * 140 registered faces the last 40 were simply not there, and nothing on screen said so: the
 * list ended and looked complete. Finding one person meant scrolling a hundred cards, and the
 * two questions actually asked of this drawer — "who is on the watchlist?" and "is this person
 * registered?" — had no answer short of reading every card.
 *
 * ─── SERVER-SIDE, NOT A CLIENT FILTER ───
 * Both the search and the filter are the node's (`search`, `is_employee`, `is_blacklisted` on
 * /persons), so they search the WHOLE registry rather than the page that happens to be loaded.
 * A client-side filter over the first 100 rows would answer "not registered" about somebody who
 * is — the exact mistake this replaces.
 *
 * The count is the node's `pagination.total` for the same reason: it is the number of people the
 * filter matches, not the number of cards rendered, so "Showing 100 of 143" is the truth and the
 * operator knows to keep loading.
 */
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PERSON_WATCHLIST_CATEGORIES, watchlistCategoryOf } from '../common/watchlistForm';

/** Which slice of the registry the drawer is showing. */
export type PeopleRegistryFilter = 'all' | 'employees' | 'watchlist';

const FILTERS: Array<{ key: PeopleRegistryFilter; label: string; title: string }> = [
  { key: 'all', label: 'All', title: 'Everyone in the registry' },
  { key: 'employees', label: 'Staff', title: 'People with an employment record' },
  { key: 'watchlist', label: 'Watchlist', title: 'People flagged on the watchlist' },
];

/** What the count line calls the people it is counting, per filter. */
const NOUN: Record<PeopleRegistryFilter, string> = {
  all: 'registered',
  employees: 'staff',
  watchlist: 'on the watchlist',
};

export interface RegisteredPeopleToolbarProps {
  query: string;
  onQueryChange: (q: string) => void;
  filter: PeopleRegistryFilter;
  onFilterChange: (f: PeopleRegistryFilter) => void;
  /** Which watchlist category, or '' for all of them. Only meaningful under `watchlist`. */
  category?: string;
  onCategoryChange?: (c: string) => void;
  /** Cards currently rendered, and how many the node says match — the two differ when paged. */
  shown: number;
  total: number;
  loading?: boolean;
}

export default function RegisteredPeopleToolbar({
  query, onQueryChange, filter, onFilterChange, category = '', onCategoryChange,
  shown, total, loading = false,
}: RegisteredPeopleToolbarProps) {
  // "4 missing" rather than "4 on the watchlist" once a category is chosen: the count is a fact
  // about the filter, and the filter is now two of them.
  const chosen = filter === 'watchlist' ? watchlistCategoryOf(PERSON_WATCHLIST_CATEGORIES, category) : undefined;
  const noun = chosen ? chosen.label.toLowerCase() : NOUN[filter];

  return (
    <div className="mt-3 space-y-2">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search people by name…"
          aria-label="Search registered people"
          className="h-9 w-full rounded-lg border border-border bg-background pl-8 pr-8 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            title={f.title}
            aria-pressed={f.key === filter}
            onClick={() => onFilterChange(f.key)}
            className={`h-7 rounded-lg px-2.5 text-[12px] font-medium transition-colors ${
              f.key === filter
                ? 'bg-brand text-white'
                : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
        {/* The count sits with the filters because it is a fact ABOUT them: change the filter
            and this is what changed. Kept mounted while loading so the row doesn't jump. */}
        <span className="ml-auto text-[11px] text-muted-foreground">
          {loading ? 'Loading…'
            : total === 0 ? 'None'
              : shown < total ? `Showing ${shown} of ${total} ${noun}`
                : `${total} ${noun}`}
        </span>
      </div>

      {/*
        ── The category row, only under Watchlist ──
        Always visible would be three dead buttons on the two filters where nothing is flagged,
        and folding these into the row above would put "Missing" beside "Staff" as if they were
        alternatives — they are not: a category NARROWS the watchlist, it is not a fourth slice
        of the registry. The node filters on it (`blacklist_category`, indexed by migration
        055), so this searches everyone flagged and not just the page on screen.
      */}
      {filter === 'watchlist' && onCategoryChange && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-2">
          <button
            type="button"
            aria-pressed={!category}
            onClick={() => onCategoryChange('')}
            className={`h-7 rounded-lg px-2.5 text-[12px] font-medium transition-colors ${
              !category
                ? 'bg-muted text-foreground'
                : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            Any reason
          </button>
          {PERSON_WATCHLIST_CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              title={c.hint}
              aria-pressed={c.value === category}
              onClick={() => onCategoryChange(c.value === category ? '' : c.value)}
              className={`h-7 rounded-lg px-2.5 text-[12px] font-medium transition-colors ${
                c.value === category
                  ? `${c.chip} bg-muted`
                  : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export interface PeopleLoadMoreProps {
  shown: number;
  total: number;
  loading?: boolean;
  onLoadMore: () => void;
}

/**
 * The foot of the list. Rendered only when the node has more people than are on screen, so its
 * absence means "that is everybody" — which is what the old, silently truncated list could not
 * say.
 */
export function PeopleLoadMore({ shown, total, loading = false, onLoadMore }: PeopleLoadMoreProps) {
  if (shown >= total) return null;
  return (
    <button
      type="button"
      onClick={onLoadMore}
      disabled={loading}
      className="h-9 w-full rounded-lg border border-border text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
    >
      {loading ? 'Loading…' : `Load ${Math.min(total - shown, 100)} more`}
    </button>
  );
}
